// The lenders' MIS (HM2, owner changes 2026-10-27). Tabs with counts:
//   Received mails  every attachment the MIS mailbox brought (Check mailbox reads it; every 15 minutes only while "Read
//                   automatically" is on): Preview, Load MIS (into the MIS files, its journey opens), Delete (a soft
//                   delete, for a lender not known or an invalid file).
//   MIS files       only real MIS: uploads (drag and drop) and loaded mail attachments, waiting or applied; a file opens its
//                   journey in a full-width drawer (/mis/files/:id).
//   Not matched (with a CSV for the lender), Missing from MIS (mark cancelled or keep), MIS settings (each lender's mail
//   rule; the MIS mailbox for admins: server, address, app password, Test connection, Read automatically).
// A file's Preview shows its first rows with mobiles, PAN and e-mails partly hidden (every opening is logged); everything
// else shows lead codes and references only.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, CircleHelp, FileSpreadsheet, Mail, RefreshCw, Search, Settings2, Trash2, Upload, type LucideIcon } from 'lucide-react'
import { Badge, Can, ConfirmDialog, EmptyState, Modal, PageHeader, SegmentedControl, Switch, formatDateTime, notify } from '@/kit/ui'
import { apiMode } from '@/lib/hubApi'
import { download, fileState, mis, notesCsv, stageLabel, toBase64, type MisFile, type MisMailbox, type MisMissing, type MisPreview, type MisSettings, type MisUnmatchedFile } from '@/lib/misApi'
import { MisJourney } from './MisJourney'

type Tab = 'mails' | 'files' | 'not-matched' | 'missing' | 'settings'
type Name = (slug: string | null) => string
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `₹${n.toLocaleString('en-IN')}`)
const size = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`)
const matches = (words: string, ...xs: (string | null | undefined)[]) => !words || xs.some((x) => x?.toLowerCase().includes(words))

export function MisScreen() {
  const { tab: tabParam, id } = useParams()
  const tab: Tab = id || !tabParam || tabParam === 'inbox' ? 'files' : (tabParam as Tab)
  const navigate = useNavigate()
  const [settings, setSettings] = useState<MisSettings | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  const reloadSettings = () => mis.settings().then(setSettings, (e: Error) => setError(e.message))
  const changed = () => { setVersion((v) => v + 1); void reloadSettings() }
  useEffect(() => { if (apiMode()) void reloadSettings() }, [])

  if (!apiMode()) return <><PageHeader title="Lender MIS" /><EmptyState title="The MIS needs the Hub API" /></>
  const name: Name = (slug) => settings?.lenders.find((l) => l.slug === slug)?.name ?? slug ?? '—'
  const c = settings?.counts
  return (
    <>
      <PageHeader title="Lender MIS" subtitle="Each lender's MIS: received or uploaded, then choose → map → match → check → apply. Every change reaches the partner portal through the lead registry." />
      {error && <p className="field-error">{error}</p>}
      <MisTabs value={tab} onChange={(t) => navigate(`/mis/${t}`)} tabs={[
        { id: 'mails', label: 'Received mails', icon: Mail, count: c?.mailsNew, hint: 'new attachments to load', tone: 'primary' },
        { id: 'files', label: 'MIS files', icon: FileSpreadsheet, count: c?.misWaiting, hint: 'waiting to apply', tone: 'primary' },
        { id: 'not-matched', label: 'Not matched', icon: CircleHelp, count: c?.notMatched, hint: 'rows to raise with lenders', tone: 'accent' },
        { id: 'missing', label: 'Missing from MIS', icon: AlertTriangle, count: c?.missing, hint: 'disbursed loans to check', tone: 'danger' },
        { id: 'settings', label: 'MIS settings', icon: Settings2 },
      ]} />
      {tab === 'mails' && <ReceivedMails settings={settings} name={name} version={version} onChanged={changed} />}
      {tab === 'files' && <MisFiles settings={settings} name={name} version={version} onChanged={changed} />}
      {tab === 'not-matched' && <NotMatched name={name} />}
      {tab === 'missing' && <Missing />}
      {tab === 'settings' && settings && <><Can permission="mis.settings"><MailboxForm onSaved={reloadSettings} /></Can><Settings settings={settings} onSaved={reloadSettings} /></>}
      {id && <MisJourney key={id} id={id} onClose={() => navigate('/mis/files')} onChanged={changed} />}
    </>
  )
}

type TabDef = { id: Tab; label: string; icon: LucideIcon; count?: number; hint?: string; tone?: keyof typeof COUNT_TONE }
const COUNT_TONE = { primary: 'bg-primary-soft text-primary-text', accent: 'bg-accent-soft text-accent-text', danger: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' }

/** The MIS tabs as buttons with an icon and a count (coloured while something waits); ←/→ move between them. */
function MisTabs({ tabs, value, onChange }: { tabs: TabDef[]; value: Tab; onChange: (t: Tab) => void }) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.id === value)
    if (e.key === 'ArrowRight') onChange(tabs[(i + 1) % tabs.length]!.id)
    else if (e.key === 'ArrowLeft') onChange(tabs[(i - 1 + tabs.length) % tabs.length]!.id)
    else return
    e.preventDefault()
  }
  return (
    <div role="tablist" aria-label="MIS" onKeyDown={onKey} className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="mis-tabs">
      {tabs.map((t) => {
        const active = t.id === value
        const n = t.count ?? 0
        const Icon = t.icon
        return (
          <button key={t.id} type="button" role="tab" aria-selected={active} tabIndex={active ? 0 : -1} onClick={() => onChange(t.id)} title={t.hint && t.count !== undefined ? `${n} ${t.hint}` : undefined}
            className={`flex shrink-0 items-center gap-2 rounded-lg border px-3.5 py-2 text-xs font-medium transition ${active ? 'border-primary bg-primary text-white shadow-card' : 'border-slate-200 bg-white text-slate-700 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-surface-dark dark:text-slate-200'}`}>
            <Icon size={15} />
            {t.label}
            {t.count !== undefined && <span className={`min-w-[1.4rem] rounded-full px-1.5 py-0.5 text-center text-2xs font-semibold leading-none ${active ? 'bg-white/25 text-white' : n ? COUNT_TONE[t.tone ?? 'primary'] : 'bg-slate-100 text-muted dark:bg-slate-800'}`}>{n}</span>}
          </button>
        )
      })}
    </div>
  )
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="relative ml-auto w-full sm:w-72">
      <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
      <input className="input pl-8" aria-label={placeholder} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

/** A soft delete, asked first: the file leaves every list; its row stays in the Hub (who and when). */
function DeleteDialog({ file, onClose, onDone }: { file: MisFile | null; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const run = async () => {
    if (!file) return
    setBusy(true)
    try { await mis.del(file.id); notify.success('Deleted', `${file.fileName} is out of the list.`); onDone() } catch (e) { notify.error('Not deleted', (e as Error).message) } finally { setBusy(false); onClose() }
  }
  return <ConfirmDialog isOpen={Boolean(file)} onClose={onClose} onConfirm={run} busy={busy} type="danger" title="Delete this file from the list?" confirmText="Delete"
    message={file ? `${file.fileName}${file.mailFrom ? ` (from ${file.mailFrom})` : ''} leaves the list. Its content is deleted; the Hub keeps only that it arrived and who deleted it.` : ''} />
}

// ---- Received mails ----

type MailShow = 'new' | 'loaded' | 'other' | 'all'
function mailState(f: MisFile) {
  if (f.state === 'new' && f.loadedAt) return { label: 'loaded', tone: 'success' as const, detail: 'in MIS files', group: 'loaded' as const }
  if (f.state === 'new') return { label: 'new', tone: 'info' as const, detail: null, group: 'new' as const }
  if (f.state === 'used') return { label: 'applied', tone: 'success' as const, detail: null, group: 'loaded' as const }
  const s = fileState(f)
  return { label: s.label === 'refused' ? 'invalid file' : s.label, tone: s.tone, detail: s.detail, group: 'other' as const }
}

function ReceivedMails({ settings, name, version, onChanged }: { settings: MisSettings | null; name: Name; version: number; onChanged: () => void }) {
  const [files, setFiles] = useState<MisFile[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [show, setShow] = useState<MailShow>('new')
  const [q, setQ] = useState('')
  const [preview, setPreview] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<MisFile | null>(null)
  const navigate = useNavigate()
  const load = () => mis.files('mail').then(setFiles, (e: Error) => notify.error('Could not read Received mails', e.message))
  useEffect(() => { void load() }, [version])

  const check = async () => {
    setBusy(true)
    try {
      const r = await mis.readMailbox()
      notify.success('Mailbox checked', r.mails ? `${r.mails} mail${r.mails === 1 ? '' : 's'}: ${r.files} file${r.files === 1 ? '' : 's'} to load${r.noLender ? ` (${r.noLender} lender not known)` : ''}${r.refused ? `, ${r.refused} invalid` : ''}` : 'No new mail.')
      setShow('new'); onChanged()
    } catch (e) { notify.error('Mailbox not read', (e as Error).message) } finally { setBusy(false) }
  }
  const loadMis = async (f: MisFile) => {
    setBusy(true)
    try { await mis.load(f.id); onChanged(); navigate(`/mis/files/${f.id}`) } catch (e) { notify.error('Not loaded', (e as Error).message) } finally { setBusy(false) }
  }

  const box = settings?.mailbox
  const all = files ?? []
  const count = (g: MailShow) => (g === 'all' ? all.length : all.filter((f) => mailState(f).group === g).length)
  const words = q.trim().toLowerCase()
  const shown = all.filter((f) => (show === 'all' || mailState(f).group === show) && matches(words, f.fileName, f.mailFrom, f.mailSubject, name(f.lenderSlug)))
  return (
    <>
      <section className="card mb-3 flex flex-wrap items-center gap-3" data-testid="mis-mailbox-bar">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${box?.on ? 'bg-primary-soft text-primary-text' : 'bg-slate-100 text-muted dark:bg-slate-800'}`}><Mail size={17} /></span>
        <div className="min-w-0 flex-1 text-xs">
          {box?.on ? <p>MIS mailbox <b>{box.label}</b></p> : <p>No MIS mailbox yet: an admin sets it up in <Link to="/mis/settings">MIS settings</Link>.</p>}
          <p className="text-2xs text-muted">
            Read automatically: {box?.auto ? <Badge tone="success">on, every {box.everyMinutes} minutes</Badge> : <><Badge tone="gray">off</Badge> (switch it on in <Link to="/mis/settings">MIS settings</Link>)</>}
            {box?.lastReadAt ? ` · last checked ${formatDateTime(box.lastReadAt)}${box.lastResult ? `: ${box.lastResult}` : ''}` : ''}
          </p>
        </div>
        <button type="button" className="btn btn-primary" disabled={busy || !box?.on} onClick={() => void check()} data-testid="mis-read-mailbox"><RefreshCw size={15} className={busy ? 'animate-spin' : ''} /> Check mailbox now</button>
      </section>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SegmentedControl<MailShow> size="sm" ariaLabel="Show" value={show} onChange={setShow} options={[
          { value: 'new', label: `To load (${count('new')})` }, { value: 'loaded', label: `Loaded (${count('loaded')})` }, { value: 'other', label: `Invalid or removed (${count('other')})` }, { value: 'all', label: `All (${all.length})` },
        ]} />
        <SearchBox value={q} onChange={setQ} placeholder="File, sender or subject" />
      </div>
      <p className="mb-2 text-2xs text-muted">Each CSV or Excel attachment of a mail is one row. <b>Load MIS</b> puts it in the MIS files and starts its journey. A file nobody loads is deleted after {settings?.holdDays ?? 14} days.</p>

      {!files ? <p className="text-2xs text-muted">Loading…</p> : shown.length === 0 ? <EmptyState title={all.length ? 'Nothing here' : 'No mail yet'} /> : (
        <div className="card-flush overflow-x-auto">
          <table className="table" data-testid="mis-mails">
            <thead><tr><th>Received</th><th>From</th><th>Lender</th><th>Attachment</th><th className="text-right">Rows</th><th>State</th><th></th></tr></thead>
            <tbody>
              {shown.map((f) => {
                const s = mailState(f)
                return (
                  <tr key={f.id}>
                    <td className="whitespace-nowrap">{formatDateTime(f.arrivedAt)}</td>
                    <td className="text-2xs">{f.mailFrom}<span className="block max-w-xs truncate text-muted">{f.mailSubject}</span></td>
                    <td>{f.lenderSlug ? name(f.lenderSlug) : <Badge tone="warning">lender not known</Badge>}</td>
                    <td><span className="flex items-center gap-1.5"><FileSpreadsheet size={14} className="shrink-0 text-muted" />{f.fileName}</span>
                      <span className="block text-2xs text-muted">{f.kind?.toUpperCase() ?? 'not CSV / Excel'} · {size(f.sizeBytes)}</span></td>
                    <td className="text-right">{f.rowCount || '—'}</td>
                    <td><Badge tone={s.tone}>{s.label}</Badge>{s.detail && <span className="block max-w-xs text-2xs text-muted">{s.detail}</span>}</td>
                    <td className="whitespace-nowrap text-right">
                      {s.group === 'new' && <><button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreview(f.id)}>Preview</button> <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void loadMis(f)} data-testid="mis-load">Load MIS ›</button> </>}
                      {s.group === 'loaded' && <Link className="btn btn-ghost btn-sm" to={`/mis/files/${f.id}`}>Open</Link>}
                      {f.state !== 'used' && !f.loadedAt && <button type="button" className="icon-btn" aria-label={`Delete ${f.fileName}`} title="Delete from the list" onClick={() => setDeleting(f)}><Trash2 size={15} /></button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <PreviewModal id={preview} onClose={() => setPreview(null)} />
      <DeleteDialog file={deleting} onClose={() => setDeleting(null)} onDone={onChanged} />
    </>
  )
}

// ---- MIS files ----

type FileShow = 'waiting' | 'applied' | 'all'

function MisFiles({ settings, name, version, onChanged }: { settings: MisSettings | null; name: Name; version: number; onChanged: () => void }) {
  const [files, setFiles] = useState<MisFile[] | null>(null)
  const [lender, setLender] = useState('')
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<MisFile | null>(null)
  const [show, setShow] = useState<FileShow | null>(null)
  const [q, setQ] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const load = () => mis.files('mis').then(setFiles, (e: Error) => notify.error('Could not read the MIS files', e.message))
  useEffect(() => { void load() }, [version])

  const upload = async (file: File | undefined) => {
    if (!file) return
    if (settings && file.size > settings.maxMb * 1024 * 1024) { notify.error('Too big', `At most ${settings.maxMb} MB: ask the lender for a smaller file, or split it.`); return }
    setBusy(true)
    try {
      const f = await mis.upload({ lenderSlug: lender || null, fileName: file.name, contentBase64: await toBase64(file) })
      if (f.state === 'refused') notify.error('Not an MIS we can read', f.refusedReason ?? '')
      else { notify.success('Uploaded', `${f.rowCount} rows${f.formulasRemoved ? `, ${f.formulasRemoved} formulas removed` : ''}`); onChanged(); navigate(`/mis/files/${f.id}`) }
    } catch (e) {
      notify.error('Not uploaded', (e as Error).message)
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  const all = files ?? []
  const waiting = all.filter((f) => f.state === 'new').length
  const current: FileShow = show ?? (waiting ? 'waiting' : 'all')
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase()
    return all.filter((f) => (current === 'all' || (current === 'waiting') === (f.state === 'new')) && matches(words, f.fileName, f.mailFrom, f.addedBy, name(f.lenderSlug)))
  }, [all, current, q]) // eslint-disable-line react-hooks/exhaustive-deps
  const open = (f: MisFile) => navigate(`/mis/files/${f.id}`)

  return (
    <>
      <section className="card mb-3" data-testid="mis-upload">
        <div
          className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${dragging ? 'border-primary bg-primary-soft' : 'border-slate-300 dark:border-slate-600'}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (!busy) void upload(e.dataTransfer.files?.[0]) }}
        >
          <Upload size={24} className="text-primary" />
          <p className="text-xs"><b>Drop a lender's MIS here</b> (CSV or Excel, up to {settings?.maxMb ?? 5} MB), or</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <select className="input w-56" aria-label="Lender of the upload" value={lender} onChange={(e) => setLender(e.target.value)}>
              <option value="">Lender: choose in step 1</option>
              {settings?.lenders.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
            </select>
            <input ref={input} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} data-testid="mis-file-input" />
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Working…' : 'Choose a file'}</button>
          </div>
          <p className="text-2xs text-muted">The journey opens at once. A mail's MIS comes here from <Link to="/mis/mails">Received mails</Link> (Load MIS).</p>
        </div>
      </section>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SegmentedControl<FileShow> size="sm" ariaLabel="Show" value={current} onChange={setShow} options={[
          { value: 'waiting', label: `Waiting (${waiting})` }, { value: 'applied', label: `Applied (${all.length - waiting})` }, { value: 'all', label: `All (${all.length})` },
        ]} />
        <SearchBox value={q} onChange={setQ} placeholder="File, lender or sender" />
      </div>
      <p className="mb-2 text-2xs text-muted">A file nobody applies is deleted after {settings?.holdDays ?? 14} days. Click a row to open it.</p>

      {!files ? <p className="text-2xs text-muted">Loading…</p> : shown.length === 0 ? <EmptyState title={all.length ? 'Nothing here' : 'No MIS yet'} /> : (
        <div className="card-flush overflow-x-auto">
          <table className="table" data-testid="mis-files">
            <thead><tr><th>Arrived</th><th>Lender</th><th>File</th><th>From</th><th className="text-right">Rows</th><th>State</th><th></th></tr></thead>
            <tbody>
              {shown.map((f) => {
                const s = fileState(f)
                return (
                  <tr key={f.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50" onClick={() => open(f)}>
                    <td className="whitespace-nowrap">{formatDateTime(f.arrivedAt)}<span className="block text-2xs text-muted">MIS date {f.misDay}{f.reportDay ? <> · <Badge tone="warning">backdated</Badge></> : null}</span></td>
                    <td>{f.lenderSlug ? name(f.lenderSlug) : <Badge tone="warning">lender not known</Badge>}</td>
                    <td><span className="flex items-center gap-1.5"><FileSpreadsheet size={14} className="shrink-0 text-muted" />{f.fileName}</span>
                      <span className="block text-2xs text-muted">{f.kind?.toUpperCase() ?? '—'} · {size(f.sizeBytes)}{f.formulasRemoved > 0 ? ` · ${f.formulasRemoved} formulas removed` : ''}</span></td>
                    <td className="text-2xs">{f.source === 'mail' ? <>{f.mailFrom}<span className="block text-muted">{f.mailSubject}</span></> : <>upload<span className="block text-muted">{f.addedBy}</span></>}</td>
                    <td className="text-right">{f.rowCount || '—'}</td>
                    <td><Badge tone={s.tone}>{s.label}</Badge>
                      {f.counts && <span className="block text-2xs text-muted">{f.counts.update} updates · {f.counts.unchanged} unchanged · {f.counts.notMatched} not matched · {f.counts.bad} bad</span>}</td>
                    <td className="whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      {f.state === 'new'
                        ? <><button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreview(f.id)}>Preview</button> <button type="button" className="btn btn-primary btn-sm" onClick={() => open(f)}>Start ›</button> <button type="button" className="icon-btn" aria-label={`Delete ${f.fileName}`} title="Delete from the list" onClick={() => setDeleting(f)}><Trash2 size={15} /></button></>
                        : <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(f)}>View</button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <PreviewModal id={preview} onClose={() => setPreview(null)} />
      <DeleteDialog file={deleting} onClose={() => setDeleting(null)} onDone={onChanged} />
    </>
  )
}

function NotMatched({ name }: { name: (slug: string | null) => string }) {
  const [list, setList] = useState<MisUnmatchedFile[] | null>(null)
  useEffect(() => { mis.unmatched().then(setList, (e: Error) => notify.error('Could not read Not matched', e.message)) }, [])
  if (!list) return <p className="text-2xs text-muted">Loading…</p>
  if (!list.length) return <EmptyState title="Every applied row was matched" />
  const all = list.flatMap((f) => f.rows.map((r) => ({ ...r, fileName: f.fileName })))
  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-2xs text-muted">Rows we could not use, by file. Send the CSV to the lender: it has the row, their reference and the reason (never a mobile).</p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => download('mis-not-matched.csv', notesCsv(all))} data-testid="mis-unmatched-csv">Download CSV ({all.length})</button>
      </div>
      {list.map((f) => (
        <section key={f.fileId} className="card-flush mb-3 overflow-x-auto">
          <div className="flex items-center justify-between px-3 py-2 text-xs"><span><b>{name(f.lenderSlug)}</b> · {f.fileName} · MIS day {f.misDay}</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => download(`${f.fileName}-not-matched.csv`, notesCsv(f.rows.map((r) => ({ ...r, fileName: f.fileName }))))}>CSV</button></div>
          <table className="table"><thead><tr><th>Row</th><th>Kind</th><th>Our lead code</th><th>Our application ID</th><th>Lender ref</th><th>Status</th><th>Reason</th></tr></thead>
            <tbody>{f.rows.map((r) => <tr key={`${r.row}-${r.kind}`}><td>{r.row}</td><td><Badge tone={r.kind === 'bad' ? 'danger' : 'warning'}>{r.kind === 'bad' ? 'bad row' : 'not matched'}</Badge></td><td className="mono">{r.leadCode ?? '—'}</td><td className="mono text-2xs">{r.applicationId ?? '—'}</td><td className="mono">{r.lenderRef ?? '—'}</td><td>{r.status ?? '—'}</td><td className="text-2xs">{r.reason}</td></tr>)}</tbody>
          </table>
        </section>
      ))}
    </>
  )
}

function Missing() {
  const [list, setList] = useState<MisMissing[] | null>(null)
  const [asking, setAsking] = useState<{ m: MisMissing; kind: 'cancel' | 'keep' } | null>(null)
  const [busy, setBusy] = useState(false)
  const load = () => mis.missing().then(setList, (e: Error) => notify.error('Could not read Missing from MIS', e.message))
  useEffect(() => { void load() }, [])
  const act = async () => {
    if (!asking) return
    setBusy(true)
    try {
      if (asking.kind === 'cancel') { await mis.cancel(asking.m.lenderSlug, asking.m.lineKey); notify.success('Marked cancelled', 'The partner portal stops counting it (or takes it back in the next open statement).') }
      else { await mis.keep(asking.m.lenderSlug, asking.m.lineKey); notify.success('Kept', 'It stays disbursed and is not flagged again.') }
      await load()
    } catch (e) {
      notify.error('Not done', (e as Error).message)
    } finally { setBusy(false); setAsking(null) }
  }
  if (!list) return <p className="text-2xs text-muted">Loading…</p>
  return (
    <>
      <p className="mb-2 text-2xs text-muted">A disbursed loan that a later MIS of its lender no longer has, while its disbursal is within 30 days. Ask the lender: if it was cancelled, mark it cancelled; if the file just missed it, keep it.</p>
      {list.length === 0 ? <EmptyState title="Nothing missing" /> : (
        <div className="card-flush overflow-x-auto">
          <table className="table" data-testid="mis-missing">
            <thead><tr><th>Lender</th><th>Lead code</th><th>Lender ref</th><th>Disbursed</th><th>Amount</th><th>Last in an MIS</th><th>Missing since</th><th></th></tr></thead>
            <tbody>{list.map((m) => (
              <tr key={`${m.lenderSlug}/${m.lineKey}`}>
                <td>{m.lenderName ?? m.lenderSlug}</td><td className="mono">{m.personCode ?? '—'}</td><td className="mono">{m.lenderRef ?? '—'}</td><td className="mono">{m.disbursedOn ?? '—'}</td><td>{rupees(m.disbursedAmount)}</td>
                <td className="mono">{m.seenOn}</td><td className="mono">{m.missingSince}</td>
                <td className="whitespace-nowrap text-right"><button type="button" className="btn btn-danger btn-sm" onClick={() => setAsking({ m, kind: 'cancel' })}>Mark cancelled</button> <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAsking({ m, kind: 'keep' })}>Keep</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <ConfirmDialog isOpen={Boolean(asking)} onClose={() => setAsking(null)} onConfirm={act} busy={busy} type={asking?.kind === 'cancel' ? 'danger' : 'info'}
        title={asking?.kind === 'cancel' ? 'Mark this loan cancelled?' : 'Keep this loan as disbursed?'} confirmText={asking?.kind === 'cancel' ? 'Mark cancelled' : 'Keep'}
        message={asking ? (asking.kind === 'cancel' ? `${asking.m.personCode ?? 'This loan'} (${rupees(asking.m.disbursedAmount)}, ${asking.m.disbursedOn}) becomes ${stageLabel('cancelled')}: no payout on it. If its month is closed, the partner portal makes a waiting adjustment for a second person to approve.` : `${asking.m.personCode ?? 'This loan'} stays disbursed and is not flagged again.`) : ''} />
    </>
  )
}

function Settings({ settings, onSaved }: { settings: MisSettings; onSaved: () => void }) {
  return (
    <>
      <p className="mb-2 text-2xs text-muted">Which mails in the MIS mailbox are each lender's MIS: the sender (an address, or @their-domain) and, if you want, words that must be in the subject. A mail that meets no rule still comes in, marked "lender not known" (its lender is chosen in step 1 of the journey). The columns and status words are saved by the journey's step 2.</p>
      <div className="card-flush overflow-x-auto">
        <table className="table" data-testid="mis-settings">
          <thead><tr><th>Lender</th><th>Mail from</th><th>Subject words</th><th>Saved mapping</th><th></th></tr></thead>
          <tbody>{settings.lenders.map((l) => <RuleRow key={l.slug} l={l} onSaved={onSaved} />)}</tbody>
        </table>
      </div>
    </>
  )
}

function RuleRow({ l, onSaved }: { l: MisSettings['lenders'][number]; onSaved: () => void }) {
  const [from, setFrom] = useState(l.mailFrom.join(', '))
  const [words, setWords] = useState(l.subjectWords.join(', '))
  const split = (v: string) => v.split(/[,\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean)
  const changed = from !== l.mailFrom.join(', ') || words !== l.subjectWords.join(', ')
  const save = async () => {
    try { await mis.saveRule(l.slug, { mailFrom: split(from), subjectWords: split(words) }); notify.success(`${l.name}: mail rule saved`); onSaved() } catch (e) { notify.error('Not saved', (e as Error).message) }
  }
  return (
    <tr>
      <td>{l.name}<span className="block text-2xs text-muted">{l.slug}</span></td>
      <td><Can permission="mis.settings" fallback={<span className="text-2xs">{from || '—'}</span>}><input className="input w-64" aria-label={`Mail from of ${l.name}`} placeholder="mis@lender.com, @lender.com" value={from} onChange={(e) => setFrom(e.target.value)} /></Can></td>
      <td><Can permission="mis.settings" fallback={<span className="text-2xs">{words || '—'}</span>}><input className="input w-40" aria-label={`Subject words of ${l.name}`} placeholder="mis" value={words} onChange={(e) => setWords(e.target.value)} /></Can></td>
      <td className="text-2xs">{Object.keys(l.columns).length ? `${Object.keys(l.columns).length} columns, ${Object.keys(l.words).length} words · ${l.updatedBy}` : 'not yet (the first file sets it)'}</td>
      <td className="text-right"><Can permission="mis.settings">{changed && <button type="button" className="btn btn-primary btn-sm" onClick={() => void save()}>Save</button>}</Can></td>
    </tr>
  )
}

/** A file's first rows (mobiles, PAN and e-mails partly hidden), to check it is the right MIS before mapping it. */
export function PreviewModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const [p, setP] = useState<MisPreview | null>(null)
  useEffect(() => {
    setP(null)
    if (id) mis.preview(id).then(setP, (e: Error) => { notify.error('No preview', e.message); onClose() })
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Modal isOpen={id !== null} onClose={onClose} size="xl" title={p ? `${p.fileName}: first ${p.shown} of ${p.rowCount} rows` : 'Preview'}>
      {!p ? <p className="text-2xs text-muted">Loading…</p> : (
        <>
          <p className="mb-2 text-2xs text-muted">Mobiles, PAN and e-mails are partly hidden. Opening a file is written in the audit log.</p>
          <div className="max-h-[60vh] overflow-auto" data-testid="mis-preview">
            <table className="table"><thead><tr><th>#</th>{p.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>{p.rows.map((r, i) => <tr key={i}><td className="text-muted">{i + 1}</td>{r.map((c, j) => <td key={j} className="whitespace-nowrap">{c}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}

/** The MIS mailbox (admins): set it up once; Test connection signs in and counts the unread mails, reading nothing.
 *  Use this mailbox: Check mailbox reads it. Read automatically (off by default): every 15 minutes as well. */
function MailboxForm({ onSaved }: { onSaved: () => void }) {
  const [m, setM] = useState<MisMailbox | null>(null)
  const [form, setForm] = useState({ host: '', port: 993, user: '', password: '', on: false, auto: false })
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)
  useEffect(() => {
    mis.mailbox().then((x) => { setM(x); setForm({ host: x.host, port: x.port, user: x.user, password: '', on: x.on, auto: x.auto }) }, (e: Error) => notify.error('Could not read the mailbox settings', e.message))
  }, [])
  const set = (k: keyof typeof form, v: string | number | boolean) => { setForm((f) => ({ ...f, [k]: v })); setResult(null) }
  const body = () => ({ host: form.host.trim(), port: Number(form.port), user: form.user.trim(), ...(form.password ? { password: form.password } : {}) })
  const test = async () => {
    setBusy(true)
    try { setResult(await mis.testMailbox(body())) } catch (e) { setResult({ ok: false, message: (e as Error).message }) } finally { setBusy(false) }
  }
  const save = async () => {
    setBusy(true)
    try {
      const x = await mis.saveMailbox({ ...body(), on: form.on, auto: form.on && form.auto })
      setM(x); setForm((f) => ({ ...f, password: '' }))
      notify.success('MIS mailbox saved', !x.on ? 'Not in use.' : x.auto ? 'Read automatically every 15 minutes; Check mailbox reads it at once.' : 'Read only when you press Check mailbox (Received mails).')
      onSaved()
    } catch (e) { notify.error('Not saved', (e as Error).message) } finally { setBusy(false) }
  }
  if (!m) return null
  return (
    <section className="card mb-3 max-w-3xl" data-testid="mis-mailbox">
      <h2 className="section-title">MIS mailbox</h2>
      <p className="mb-2 text-2xs text-muted">The mailbox the lenders send their MIS to (e.g. mis@ on the company mail). IMAP must be on for it; use an app password, not a person's own. Each CSV or Excel attachment goes into <b>Received mails</b>, where you load it as an MIS; the mail is then deleted from the mailbox.</p>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <label className="text-2xs">IMAP server<input className="input mt-1" aria-label="IMAP server" placeholder="imap.gmail.com" value={form.host} onChange={(e) => set('host', e.target.value)} /></label>
        <label className="text-2xs">Port (TLS)<input className="input mt-1" aria-label="Port" type="number" value={form.port} onChange={(e) => set('port', Number(e.target.value))} /></label>
        <label className="text-2xs">Mailbox address<input className="input mt-1" aria-label="Mailbox address" placeholder="mis@firstfundfincorp.com" value={form.user} onChange={(e) => set('user', e.target.value)} /></label>
        <label className="text-2xs">App password<input className="input mt-1" aria-label="App password" type="password" autoComplete="new-password" placeholder={m.passwordSet ? 'saved: type only to change it' : ''} value={form.password} onChange={(e) => set('password', e.target.value)} /></label>
      </div>
      <div className="mt-3 space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <Switch checked={form.on} onChange={(v) => set('on', v)} label="Use this mailbox" description="Check mailbox (in Received mails) reads it when you press it." />
        <Switch checked={form.on && form.auto} disabled={!form.on} onChange={(v) => set('auto', v)} label="Read automatically every 15 minutes" description="Off by default. On: new mails land in Received mails by themselves; you still press Load MIS on each." />
      </div>
      {result && <p className={`mt-2 text-xs ${result.ok ? 'text-success' : 'field-error'}`} data-testid="mis-mailbox-test">{result.message}</p>}
      <div className="mt-3 flex gap-2">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void test()}>Test connection</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>Save</button>
      </div>
      {m.updatedAt && <p className="mt-2 text-2xs text-muted">Last changed {formatDateTime(m.updatedAt)} by {m.updatedBy}</p>}
    </section>
  )
}
